import type { Locale } from "@/types";
import type { AgentId } from "@/data/experience-content";

export interface AgentDestinationProfile {
  id: AgentId;
  name: string;
  promise: string;
  task: string;
}

export interface IntegrationExperience {
  id: "commerce-context" | "creative-evidence" | "asset-libraries" | "team-handoffs";
  title: string;
  summary: string;
  incoming: readonly string[];
  preserved: string;
  destination: string;
}

interface DestinationCopy {
  common: {
    earlyAccess: string;
    planned: string;
    humanReview: string;
    connectedContext: string;
    explore: string;
    learnMore: string;
  };
  agents: {
    eyebrow: string;
    title: string;
    description: string;
    managerNote: string;
    specialistLabel: string;
    detailLabels: readonly [string, string, string, string, string];
    teamTitle: string;
    nextAgent: string;
    visualCta: string;
    profiles: readonly AgentDestinationProfile[];
  };
  integrations: {
    eyebrow: string;
    title: string;
    description: string;
    principleTitle: string;
    principleBody: string;
    labels: readonly [string, string, string];
    items: readonly IntegrationExperience[];
  };
  useCases: {
    eyebrow: string;
    title: string;
    description: string;
    detailLabels: readonly [string, string, string, string, string];
    workflowTitle: string;
  };
  resources: {
    eyebrow: string;
    title: string;
    description: string;
    featuredLabel: string;
    featuredTitle: string;
    featuredBody: string;
    featuredAction: string;
    directoryTitle: string;
    groups: readonly {
      title: string;
      items: readonly { href: string; title: string; description: string; icon: "article" | "lifebuoy" | "plugs" | "clock" | "building" | "chat" }[];
    }[];
  };
  closing: { label: string; title: string };
}

export const destinationContent: Record<Locale, DestinationCopy> = {
  en: {
    common: { earlyAccess: "Request early access", planned: "Planned", humanReview: "Human review", connectedContext: "Shared Brand Context", explore: "Explore", learnMore: "Learn more" },
    agents: {
      eyebrow: "One coordinated team",
      title: "Five agents. One creative chain.",
      description: "Each agent owns a distinct decision. Vera keeps the brief, evidence, review state, and final delivery moving as one piece of work.",
      managerNote: "Vera directs the work and brings every specialist result back into one reviewable plan.",
      specialistLabel: "Specialist agents",
      detailLabels: ["Starts with", "How this agent decides", "Delivers", "Hands off", "You stay in control"],
      teamTitle: "The work never loses its context.",
      nextAgent: "Meet another agent",
      visualCta: "Open image generator",
      profiles: [
        { id: "project-manager", name: "Vera", promise: "Turns your brief into a clear plan.", task: "Understands your brand and goal, breaks the work into specialist tasks, and brings every result back together." },
        { id: "creative-analyst", name: "Evan", promise: "Finds the evidence worth learning from.", task: "Searches relevant video signals, breaks down the patterns behind them, and turns the findings into brand-specific creative directions." },
        { id: "creative-producer", name: "Luca", promise: "Turns a direction into a script ready to shoot.", task: "Builds an original, brand-ready script from approved references, with the hook, scene flow, production guidance, and CTA clearly defined." },
        { id: "influencer-manager", name: "Siena", promise: "Finds creators who fit the work.", task: "Filters creators by platform, audience, content style, public performance, and brand fit, then explains every recommendation." },
        { id: "image-generator", name: "Mira", promise: "Turns your idea into a visual you can refine.", task: "Creates product images, campaign visuals, and visual concepts from text or references, then refines subject, setting, composition, color, and style with you." },
      ],
    },
    integrations: {
      eyebrow: "Integration roadmap",
      title: "Connect the context, not just the files.",
      description: "The connections we are designing keep product facts, evidence, assets, and decisions attached to the work. Every item below is a roadmap direction, not a claim of current availability.",
      principleTitle: "Information should arrive with meaning intact.",
      principleBody: "A useful integration carries the source, brand constraints, review state, and intended destination together, so agents can make a decision your team can inspect.",
      labels: ["Information in", "Context preserved", "Moves to"],
      items: [
        { id: "commerce-context", title: "Commerce context", summary: "Bring verified product facts into the same Brand Context used by every agent.", incoming: ["Catalog and variants", "Markets and offers", "Approved product claims"], preserved: "Source, market, approval status, and product constraints", destination: "Brand Profile and project briefs" },
        { id: "creative-evidence", title: "Creative evidence", summary: "Keep reference media and public performance context attached to the question it is meant to answer.", incoming: ["Social references", "Advertising examples", "Public performance signals"], preserved: "Original link, platform context, selection reason, and evidence date", destination: "Creative Analyst evidence queue" },
        { id: "asset-libraries", title: "Asset libraries", summary: "Help the team find approved material without separating it from usage rules and campaign history.", incoming: ["Product footage", "Previous campaigns", "Brand guidelines"], preserved: "Ownership, approval, usage notes, and campaign relationship", destination: "Creative Producer and Visual Designer" },
        { id: "team-handoffs", title: "Team handoffs", summary: "Move review state and structured deliverables into the tools where teams coordinate execution.", incoming: ["Approvals and comments", "Scripts and creator briefs", "Project status"], preserved: "Decision history, owner, version, and next action", destination: "Project Manager delivery and team workflow" },
      ],
    },
    useCases: {
      eyebrow: "Built around the team problem",
      title: "A workflow for every team that ships creative.",
      description: "Start with the work your team is responsible for. Zesrin connects the right context, agent decisions, and reviewable deliverables around it.",
      detailLabels: ["The team problem", "Context required", "Agent collaboration", "Reviewable deliverables", "The next feedback loop"],
      workflowTitle: "One visible route from question to delivery.",
    },
    resources: {
      eyebrow: "Resources",
      title: "Keep the creative conversation moving.",
      description: "Research, product guidance, and practical paths for teams turning market evidence into work they can ship.",
      featuredLabel: "Featured field note",
      featuredTitle: "Start with evidence, not another blank brainstorm",
      featuredBody: "A repeatable way to move from signals and proven videos to an original creative direction.",
      featuredAction: "Read the field note",
      directoryTitle: "Find the next useful thing.",
      groups: [
        { title: "Learn", items: [{ href: "/blog", title: "Blog", description: "Research notes, creative teardowns, and practical playbooks.", icon: "article" }, { href: "/help", title: "Get help", description: "Set up Brand Context and run your first project.", icon: "lifebuoy" }] },
        { title: "Product", items: [{ href: "/integrations", title: "Integrations", description: "See how evidence, assets, and workflows are planned to connect.", icon: "plugs" }, { href: "/releases", title: "Release notes", description: "A clear record of product updates and improvements.", icon: "clock" }] },
        { title: "Company", items: [{ href: "/about", title: "About us", description: "Why we are building a better creative decision system.", icon: "building" }, { href: "/contact", title: "Contact us", description: "Talk with the team about access, partnerships, or support.", icon: "chat" }] },
      ],
    },
    closing: { label: "Bring your brand context", title: "Build the next decision with evidence attached." },
  },
  zh: {
    common: { earlyAccess: "申请早期体验", planned: "规划中", humanReview: "人工审核", connectedContext: "共享品牌 Context", explore: "查看详情", learnMore: "进一步了解" },
    agents: {
      eyebrow: "一支协同团队",
      title: "五个 Agent，一条完整创意链路。",
      description: "每个 Agent 负责一个明确判断。Vera 让 Brief、证据、审核状态与最终交付始终属于同一项工作。",
      managerNote: "Vera 负责调度工作，并把每位专家的结果重新组织成可审核的完整计划。",
      specialistLabel: "专业 Agent",
      detailLabels: ["从这些信息开始", "Agent 如何判断", "结构化交付", "交给下一环节", "由你保持控制"],
      teamTitle: "工作流转，背景不会丢失。",
      nextAgent: "认识下一位 Agent",
      visualCta: "打开图片生成器",
      profiles: [
        { id: "project-manager", name: "Vera", promise: "把你的 Brief 变成清晰计划。", task: "理解品牌与目标，拆解专业任务并分配工作，最后把每项结果重新组织成完整交付。" },
        { id: "creative-analyst", name: "Evan", promise: "找到真正值得借鉴的证据。", task: "搜索相关视频信号，拆解背后的有效模式，并把发现转化为适合品牌的创意方向。" },
        { id: "creative-producer", name: "Luca", promise: "把方向变成可以开拍的脚本。", task: "根据已批准的参考构建原创品牌脚本，明确 Hook、场景节奏、制作指导和 CTA。" },
        { id: "influencer-manager", name: "Siena", promise: "找到真正适合这项创意的 Creator。", task: "按平台、受众、内容风格、公开表现与品牌适配筛选 Creator，并解释每一项推荐依据。" },
        { id: "image-generator", name: "Mira", promise: "把想法变成可以持续调整的视觉。", task: "根据文字或参考图创建产品图、Campaign 视觉与概念图，并与你一起调整主体、场景、构图、色彩和风格。" },
      ],
    },
    integrations: {
      eyebrow: "集成规划",
      title: "连接完整背景，而不只是传输文件。",
      description: "我们正在设计的连接会让产品事实、证据、素材和决策跟随工作流转。以下均为规划方向，不代表当前已经上线。",
      principleTitle: "信息进入系统时，意义也应完整保留。",
      principleBody: "有效的集成会同时携带来源、品牌限制、审核状态与使用目的，让 Agent 的每个判断都可以被团队检查。",
      labels: ["进入系统", "保留背景", "流向"],
      items: [
        { id: "commerce-context", title: "商业信息", summary: "把经过确认的产品事实带入所有 Agent 共用的 Brand Context。", incoming: ["商品目录与规格", "市场与 Offer", "已批准的产品表述"], preserved: "来源、市场、审核状态与产品限制", destination: "品牌档案与项目 Brief" },
        { id: "creative-evidence", title: "创意证据", summary: "让参考内容和公开表现背景始终关联它需要回答的品牌问题。", incoming: ["社交内容参考", "广告案例", "公开表现信号"], preserved: "原始链接、平台背景、选择理由与证据日期", destination: "创意分析师的证据队列" },
        { id: "asset-libraries", title: "素材库", summary: "帮助团队找到已批准素材，同时保留使用规则与 Campaign 历史。", incoming: ["产品素材", "历史 Campaign", "品牌规范"], preserved: "归属、审核、使用说明与 Campaign 关系", destination: "创意制片人与视觉设计师" },
        { id: "team-handoffs", title: "团队交接", summary: "将审核状态与结构化交付带入团队日常协调执行的工具。", incoming: ["审核与评论", "脚本与 Creator Brief", "项目状态"], preserved: "决策历史、负责人、版本与下一步动作", destination: "项目经理交付与团队工作流" },
      ],
    },
    useCases: {
      eyebrow: "围绕团队问题设计",
      title: "为每一支交付创意的团队连接工作流。",
      description: "从团队真正负责的工作开始，Zesrin 将匹配的背景、Agent 判断和可审核交付连接起来。",
      detailLabels: ["团队面对的问题", "所需背景", "Agent 协作", "可审核交付", "下一轮反馈"],
      workflowTitle: "从问题到交付，每一步都清晰可见。",
    },
    resources: {
      eyebrow: "资源",
      title: "让创意讨论持续向前。",
      description: "为团队提供研究、产品指南与实用入口，把市场证据变成真正可以交付的工作。",
      featuredLabel: "精选文章",
      featuredTitle: "先找到证据，而不是再次从空白开始头脑风暴",
      featuredBody: "一套可复用的方法，从市场信号和有效视频走向原创创意方向。",
      featuredAction: "阅读文章",
      directoryTitle: "找到此刻最有用的内容。",
      groups: [
        { title: "学习", items: [{ href: "/blog?lang=zh", title: "博客", description: "创意研究、内容拆解与实用方法。", icon: "article" }, { href: "/help", title: "帮助中心", description: "建立 Brand Context 并启动第一个项目。", icon: "lifebuoy" }] },
        { title: "产品", items: [{ href: "/integrations", title: "集成", description: "了解证据、素材与工作流的连接规划。", icon: "plugs" }, { href: "/releases", title: "更新记录", description: "清晰记录产品更新与改进。", icon: "clock" }] },
        { title: "公司", items: [{ href: "/about", title: "关于我们", description: "了解我们为何构建更好的创意决策系统。", icon: "building" }, { href: "/contact", title: "联系我们", description: "沟通体验申请、合作或支持问题。", icon: "chat" }] },
      ],
    },
    closing: { label: "带上你的品牌背景", title: "让下一个创意决策始终带着证据。" },
  },
};
